import React, { useState, useEffect } from 'react';
import { useInactivityTimer } from '../hooks/useInactivityTimer';
import { useAuth } from '../context/AuthContext';
import MFAEnrollmentFlow from '../components/MFAEnrollmentFlow';
import Button from '../components/Button';
import api from '../services/api';

export default function AccountPage() {
  useInactivityTimer();
  const { user: currentUser } = useAuth();
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [revokingDeviceId, setRevokingDeviceId] = useState(null);
  const [showEnrollment, setShowEnrollment] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const [meRes, devicesRes] = await Promise.all([
          api.get('/users/me'),
          api.get('/auth/devices'),
        ]);
        setMfaEnabled(meRes.data.mfa_enabled);
        setDevices(devicesRes.data);
      } catch {
        setMfaEnabled(currentUser?.mfa_enabled || false);
      } finally {
        setStatusLoading(false);
        setDevicesLoading(false);
      }
    };
    fetchStatus();
  }, [currentUser?.mfa_enabled]);

  const handleBegin = async () => {
    const response = await api.post('/mfa/enrollment/begin');
    return response.data;
  };

  const handleConfirm = async (secret, totpCode) => {
    const response = await api.post('/mfa/enrollment/confirm', {
      secret,
      totp_code: totpCode,
    });
    return response.data;
  };

  const handleEnrollmentSuccess = async () => {
    try {
      const res = await api.get('/users/me');
      setMfaEnabled(res.data.mfa_enabled);
    } catch {
      setMfaEnabled(true);
    }
    setShowEnrollment(false);
    setSuccess('MFA enrollment complete. Your account is now protected.');
  };

  const handleEnrollmentCancel = () => {
    setShowEnrollment(false);
  };

  const handleDisableMfa = async () => {
    if (!window.confirm('Are you sure you want to disable MFA? This will remove your authenticator app connection and delete your recovery codes.')) return;
    setError(''); setLoading(true);
    try {
      await api.delete('/mfa/enrollment');
      setMfaEnabled(false);
      setSuccess('MFA has been disabled.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to disable MFA.');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeDevice = async (deviceId) => {
    setRevokingDeviceId(deviceId);
    try {
      await api.delete(`/auth/devices/${deviceId}`);
      setDevices(prev => prev.filter(d => d.id !== deviceId));
    } catch {
      setError('Failed to revoke device.');
    } finally {
      setRevokingDeviceId(null);
    }
  };

  const handleRevokeAllDevices = async () => {
    if (!window.confirm('Remove trust from all devices? You will need to complete MFA on your next login from every device.')) return;
    try {
      await api.delete('/auth/devices');
      localStorage.removeItem('device_token');
      setDevices([]);
      setSuccess('All trusted devices removed.');
    } catch {
      setError('Failed to revoke devices.');
    }
  };

  const formatUserAgent = (ua) => {
    if (!ua) return 'Unknown device';
    let device = 'Computer';
    if (ua.includes('iPhone')) device = 'iPhone';
    else if (ua.includes('iPad')) device = 'iPad';
    else if (ua.includes('Android') && ua.includes('Mobile')) device = 'Android Phone';
    else if (ua.includes('Android')) device = 'Android Tablet';
    else if (ua.includes('Macintosh')) device = 'Mac';
    else if (ua.includes('Windows')) device = 'Windows PC';
    else if (ua.includes('Linux')) device = 'Linux';

    let browser = 'Browser';
    if (ua.includes('Edg/') || ua.includes('Edge/')) browser = 'Edge';
    else if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari')) browser = 'Safari';

    return `${device} · ${browser}`;
  };

  const formatDate = (isoString) => {
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  return (
    <div>
      <h1 style={styles.pageTitle}>My Account</h1>
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div style={styles.userInfo}>
            <div style={styles.userName}>
              {currentUser?.first_name} {currentUser?.last_name}
              {currentUser?.credential ? `, ${currentUser.credential}` : ''}
            </div>
            <div style={styles.userMeta}>
              {currentUser?.email}
            </div>
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Security</h2>
          <p style={styles.sectionDescription}>
            Two-factor authentication (MFA) adds a second layer of protection by requiring
            a code from your authenticator app each time you log in.
          </p>

          {error && <div style={styles.errorBanner}>{error}</div>}
          {success && <div style={styles.successBanner}>{success}</div>}

          {statusLoading ? (
            <div style={styles.statusLoading}>Loading...</div>
          ) : (
            <>
              <div style={styles.mfaStatusCard}>
                <div style={styles.mfaStatusLeft}>
                  <div style={styles.mfaStatusLabel}>Two-Factor Authentication</div>
                  <div style={styles.mfaStatusSub}>
                    {mfaEnabled
                      ? 'Your account is protected with an authenticator app.'
                      : 'Not enabled. We strongly recommend enabling MFA before accessing patient data.'}
                  </div>
                </div>
                <span style={mfaEnabled ? styles.badgeMfaOn : styles.badgeMfaOff}>
                  {mfaEnabled ? 'Enabled' : 'Not enabled'}
                </span>
              </div>

              {!showEnrollment && (
                <div style={styles.mfaActions}>
                  {!mfaEnabled && (
                    <Button onClick={() => setShowEnrollment(true)}>
                      Set Up Authenticator App
                    </Button>
                  )}
                  {mfaEnabled && (
                    <>
                      <Button onClick={() => setShowEnrollment(true)}>
                        Re-enroll Authenticator App
                      </Button>
                      <Button variant="danger" onClick={handleDisableMfa} disabled={loading}>
                        Disable MFA
                      </Button>
                    </>
                  )}
                </div>
              )}

              {showEnrollment && (
                <MFAEnrollmentFlow
                  onBegin={handleBegin}
                  onConfirm={handleConfirm}
                  onSuccess={handleEnrollmentSuccess}
                  onCancel={handleEnrollmentCancel}
                />
              )}
            </>
          )}
        </div>

        {/* Trusted devices section */}
        <div style={{ ...styles.section, borderTop: '1px solid var(--color-border)' }}>
          <h2 style={styles.sectionTitle}>Trusted Devices</h2>
          <p style={styles.sectionDescription}>
            Devices that have completed MFA verification recently.
            Removing a device will require MFA on the next login from that device.
          </p>

          {devicesLoading ? (
            <div style={styles.statusLoading}>Loading...</div>
          ) : devices.length === 0 ? (
            <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)' }}>
              No trusted devices.
            </p>
          ) : (
            <>
              <table style={styles.deviceTable}>
                <thead>
                  <tr>
                    {['Device', 'IP Address', 'Trusted Until', ''].map(h => (
                      <th key={h} style={styles.deviceTh}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {devices.map(d => (
                    <tr key={d.id} style={styles.deviceTr}>
                      <td style={styles.deviceTd}>{formatUserAgent(d.user_agent)}</td>
                      <td style={styles.deviceTd}>{d.ip_address || '—'}</td>
                      <td style={styles.deviceTd}>{formatDate(d.trusted_until)}</td>
                      <td style={styles.deviceTd}>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleRevokeDevice(d.id)}
                          disabled={revokingDeviceId === d.id}
                        >
                          {revokingDeviceId === d.id ? 'Removing...' : 'Remove'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {devices.length > 1 && (
                <Button variant="danger" onClick={handleRevokeAllDevices}>
                  Remove All Trusted Devices
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  pageTitle: { fontSize: 'var(--font-size-xl)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)', margin: '0 0 20px', fontFamily: 'var(--font-family-base)' },
  card: { backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-sm)', overflow: 'hidden', maxWidth: '640px' },
  cardHeader: { padding: '20px 24px', borderBottom: '1px solid var(--color-border)', backgroundColor: 'var(--color-bg-subtle)' },
  userInfo: { display: 'flex', flexDirection: 'column', gap: '4px' },
  userName: { fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-base)' },
  userMeta: { fontSize: '13px', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)' },
  section: { padding: '24px' },
  sectionTitle: { fontSize: '15px', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)', margin: '0 0 6px', fontFamily: 'var(--font-family-base)' },
  sectionDescription: { fontSize: '13px', color: 'var(--color-text-muted)', lineHeight: '1.6', margin: '0 0 20px', fontFamily: 'var(--font-family-base)' },
  statusLoading: { fontSize: '13px', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)' },
  errorBanner: { backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 'var(--radius-sm)', padding: '10px 14px', fontSize: '13px', color: '#991b1b', marginBottom: '16px', fontFamily: 'var(--font-family-base)' },
  successBanner: { backgroundColor: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 'var(--radius-sm)', padding: '10px 14px', fontSize: '13px', color: '#065f46', marginBottom: '16px', fontFamily: 'var(--font-family-base)' },
  mfaStatusCard: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', marginBottom: '20px', backgroundColor: 'var(--color-bg-subtle)' },
  mfaStatusLeft: { display: 'flex', flexDirection: 'column', gap: '4px' },
  mfaStatusLabel: { fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-base)' },
  mfaStatusSub: { fontSize: '13px', color: 'var(--color-text-muted)', maxWidth: '380px', fontFamily: 'var(--font-family-base)' },
  badgeMfaOn:  { backgroundColor: 'var(--color-status-transplant)', color: '#FFFFFF', padding: '2px 10px', borderRadius: 'var(--radius-full)', fontSize: '11px', fontWeight: 'var(--font-weight-medium)', whiteSpace: 'nowrap' },
  badgeMfaOff: { backgroundColor: 'var(--color-status-inactive)', color: 'var(--color-status-inactive-text)', padding: '2px 10px', borderRadius: 'var(--radius-full)', fontSize: '11px', fontWeight: 'var(--font-weight-medium)', whiteSpace: 'nowrap' },
  mfaActions: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  deviceTable: { width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '16px' },
  deviceTh: { textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid var(--color-border)', fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'var(--font-family-base)' },
  deviceTr: { borderBottom: '1px solid var(--color-subNav)' },
  deviceTd: { padding: '10px 12px', color: 'var(--color-text-primary)', verticalAlign: 'middle', fontFamily: 'var(--font-family-base)' },
};